/**
 * RedirectWithParams -- Navigate replacement that preserves source search params.
 *
 * When redirecting old routes to new routes, source query params (?msg_id=, ?period=)
 * are merged into the destination URL. If the destination already specifies a param
 * (e.g., ?tab=costs), it takes precedence over source params with the same key.
 */

import { Navigate, useLocation } from 'react-router';

interface RedirectWithParamsProps {
  /** Destination path, may include query string (e.g., "/admin/reports?tab=builder") */
  to: string;
}

export function RedirectWithParams({ to }: RedirectWithParamsProps) {
  const location = useLocation();

  // Parse destination into path + params
  const qIdx = to.indexOf('?');
  const destPath = qIdx >= 0 ? to.slice(0, qIdx) : to;
  const destSearch = qIdx >= 0 ? to.slice(qIdx + 1) : '';
  const destParams = new URLSearchParams(destSearch);

  // Source params from current location
  const sourceParams = new URLSearchParams(location.search);

  // Merge: source params first, then dest params override
  const merged = new URLSearchParams();
  sourceParams.forEach((value, key) => {
    merged.set(key, value);
  });
  destParams.forEach((value, key) => {
    merged.set(key, value); // destination takes precedence
  });

  const search = merged.toString();
  const finalPath = search ? `${destPath}?${search}` : destPath;

  return <Navigate to={finalPath} replace />;
}
